import { useState, useEffect } from "react";

import { Shield, ShieldCheck, ShieldAlert, RefreshCw, Copy, AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/contexts/AuthContext";

import { TwoFactorDialog } from "./TwoFactorDialog";

export const TwoFactorManagement = () => {
  const { user } = useAuth();
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [isDisabling, setIsDisabling] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);
  
  // State for enabling 2FA
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isEnabling, setIsEnabling] = useState(false);

  useEffect(() => {
    if (user) {
      // Better Auth stores 2FA status in user.twoFactorEnabled
      setIs2FAEnabled(user.twoFactorEnabled || false);
      setIsLoading(false);
    }
  }, [user]);

  const handleEnable2FA = async () => {
    setIsEnabling(true);
    try {
      // Better Auth's enable method returns the TOTP URI and backup codes
      const { data, error } = await authClient.twoFactor.enable({
        password: password // Better Auth might require password to enable
      });

      if (error) throw error;

      setQrCode(data.totpURI);
      setSecret(data.secret);
      setBackupCodes(data.backupCodes || []);
      setShowEnableDialog(true);
    } catch (error: any) {
      console.error("Error enabling 2FA:", error);
      toast.error(error.message || "Failed to enable 2FA");
    } finally {
      setIsEnabling(false);
    }
  };

  const verifyAndEnable = async () => {
    try {
      // Better Auth's verifyTotp method
      const { error } = await authClient.twoFactor.verifyTotp({
        code: verificationCode
      });

      if (error) throw error;

      toast.success("2FA enabled successfully");
      setIs2FAEnabled(true);
      setShowEnableDialog(false);
      setShowBackupCodesDialog(true); // Show backup codes after successful enable
    } catch (error: any) {
      console.error("Error verifying 2FA:", error);
      toast.error(error.message || "Invalid verification code");
    }
  };

  const handleDisable2FA = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setIsDisabling(true);
    try {
      // Better Auth's disable method
      const { error } = await authClient.twoFactor.disable({
        password,
      });

      if (error) throw error;

      toast.success("2FA has been disabled");
      setIs2FAEnabled(false);
      setShowDisableDialog(false);
      setPassword("");
    } catch (error: any) {
      console.error("Error disabling 2FA:", error);
      toast.error(error.message || "Failed to disable 2FA");
    } finally {
      setIsDisabling(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    navigator.clipboard.writeText(codesText);
    setBackupCodesCopied(true);
    toast.success("Backup codes copied to clipboard");
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Loading 2FA settings...</p>
        </CardContent>
      </Card>
    );
  }

  if (!is2FAEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Protect your account by requiring a code from your authenticator app when logging in.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label htmlFor="enable-password">Confirm Password to Enable</Label>
              <Input 
                id="enable-password" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>

            <Button onClick={handleEnable2FA} disabled={isEnabling || !password}>
              {isEnabling ? "Setting up..." : "Enable 2FA"}
            </Button>
          </div>

          {/* Enable Dialog */}
          <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Scan QR Code</DialogTitle>
                <DialogDescription>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex flex-col items-center gap-4 py-4">
                {qrCode && (
                   // Note: In a real app, use a QR code library. For now, we display the secret.
                   // Better Auth returns a URI string (otpauth://...).
                   <div className="p-4 bg-white rounded-lg">
                     {/* Placeholder for QR Code - user would need a library like qrcode.react */}
                     <p className="text-xs text-center mb-2">Scan in app:</p>
                     <code className="text-xs break-all">{secret}</code>
                   </div>
                )}
                
                <div className="w-full space-y-2">
                  <Label htmlFor="verify-code">Verification Code</Label>
                  <Input
                    id="verify-code"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEnableDialog(false)}>Cancel</Button>
                <Button onClick={verifyAndEnable} disabled={!verificationCode}>Verify & Enable</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-600" />
            Two-Factor Authentication Enabled
          </CardTitle>
          <CardDescription>
            Your account is protected with 2FA
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              Two-factor authentication is active. You'll need to enter a code from your authenticator app each time you log in.
            </AlertDescription>
          </Alert>
          
          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={() => setShowDisableDialog(true)}
            >
              <ShieldAlert className="h-4 w-4 mr-2" />
              Disable 2FA
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password to confirm you want to disable 2FA. This will remove the extra layer of security from your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDisableDialog(false);
                setPassword("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={isDisabling || !password}
            >
              {isDisabling ? "Disabling..." : "Disable 2FA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Backup Codes</DialogTitle>
            <DialogDescription>
              Save these codes in a secure location. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="font-semibold">
              This is the only time you'll see these codes. Save them now!
            </AlertDescription>
          </Alert>

          <div className="bg-muted p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {backupCodes.map((code, index) => (
                <div key={index} className="p-2 bg-background rounded border">
                  {code}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={copyBackupCodes}
              className="w-full sm:w-auto"
            >
              <Copy className="h-4 w-4 mr-2" />
              {backupCodesCopied ? "Copied!" : "Copy All Codes"}
            </Button>
            <Button
              onClick={() => setShowBackupCodesDialog(false)}
              className="w-full sm:w-auto"
              disabled={!backupCodesCopied}
            >
              I've Saved My Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
